import React, { useId, useState } from "react";

import { Btn, Drawer, Field, Icon, Select, cx } from "../catalogProductStudio/edit/StudioUi";
import { EMPTY_FILTERS, countActiveFilters, fill, plural } from "./metaAdsModel";

const FAMILY_LABEL_KEYS = {
  shoes: "catalogProductStudio.familyShoes",
  textile: "catalogProductStudio.familyTextile",
  accessories: "catalogProductStudio.familyAccessories",
};

export const SORT_OPTIONS = ["newest", "oldest", "name", "price_asc", "price_desc", "stock_desc"];

/** Option lists for every filter, built from the picker's facets. */
const useFilterOptions = (facets, t, tt) => {
  const familyLabel = (value) => (FAMILY_LABEL_KEYS[value] ? t(FAMILY_LABEL_KEYS[value]) : value);
  return {
    categoryId: (facets?.categories || []).map((entry) => ({
      value: String(entry.id),
      label: `${entry.name} (${entry.count})`,
      chip: entry.name,
    })),
    brandId: (facets?.brands || []).map((entry) => ({
      value: String(entry.id),
      label: `${entry.name} (${entry.count})`,
      chip: entry.name,
    })),
    family: (facets?.families || []).map((entry) => ({
      value: entry.value,
      label: `${familyLabel(entry.value)} (${entry.count})`,
      chip: familyLabel(entry.value),
    })),
    stock: ["in_stock", "low_stock", "out_of_stock"].map((value) => ({
      value,
      label: tt(`filterStock_${value}`),
      chip: tt(`filterStock_${value}`),
    })),
    sale: ["on_sale", "full_price"].map((value) => ({
      value,
      label: tt(`filterSale_${value}`),
      chip: tt(`filterSale_${value}`),
    })),
    addedWithinDays: ["7", "30", "90"].map((value) => ({
      value,
      label: fill(tt("filterAddedWithin"), { days: value }),
      chip: fill(tt("filterAddedWithin"), { days: value }),
    })),
    meta: ["reaching", "not_reaching"].map((value) => ({
      value,
      label: tt(`filterMeta_${value}`),
      chip: tt(`filterMeta_${value}`),
    })),
  };
};

const FILTER_FIELDS = [
  { key: "categoryId", labelKey: "filterCategory", allKey: "filterCategoryAll" },
  { key: "brandId", labelKey: "filterBrand", allKey: "filterBrandAll" },
  { key: "family", labelKey: "filterFamily", allKey: "filterFamilyAll" },
  { key: "addedWithinDays", labelKey: "filterAdded", allKey: "filterAddedAll" },
  { key: "stock", labelKey: "filterStock", allKey: "filterStockAll" },
  { key: "sale", labelKey: "filterSale", allKey: "filterSaleAll" },
  { key: "meta", labelKey: "filterMeta", allKey: "filterMetaAll" },
];

const ProductPickerToolbar = ({
  t,
  tt,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  layout,
  onLayoutChange,
  facets,
  resultCount,
  searchOnly = false,
}) => {
  const searchId = useId();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const options = useFilterOptions(facets, t, tt);
  const activeCount = countActiveFilters(filters);

  const setFilter = (key, value) => onFiltersChange({ ...filters, [key]: value });
  const clearFilters = () => onFiltersChange({ ...EMPTY_FILTERS, search: filters.search });

  const renderSelect = (field, className) => (
    <Select
      value={filters[field.key]}
      onChange={(value) => setFilter(field.key, value)}
      aria-label={tt(field.labelKey)}
      className={cx("mka-toolbar__select", filters[field.key] && "is-active", className)}
    >
      <option value="">{tt(field.allKey)}</option>
      {options[field.key].map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );

  const chips = FILTER_FIELDS.filter((field) => filters[field.key]).map((field) => {
    const option = options[field.key].find((entry) => entry.value === String(filters[field.key]));
    return { field, label: option?.chip || filters[field.key] };
  });

  return (
    <div className="mka-toolbar">
      <div className="mka-toolbar__row">
        <div className="mka-search">
          <label htmlFor={searchId} className="pse-visually-hidden">
            {tt("searchLabel")}
          </label>
          <Icon name="search" size={17} className="mka-search__icon" />
          <input
            id={searchId}
            type="search"
            className="pse-input mka-search__input"
            value={filters.search}
            placeholder={tt("searchPlaceholder")}
            autoComplete="off"
            onChange={(event) => setFilter("search", event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && filters.search) {
                event.stopPropagation();
                setFilter("search", "");
              }
            }}
          />
          {filters.search ? (
            <button
              type="button"
              className="mka-search__clear"
              aria-label={tt("clearSearch")}
              onClick={() => setFilter("search", "")}
            >
              <Icon name="x" size={15} />
            </button>
          ) : null}
        </div>

        {searchOnly ? null : (
          <>
            {renderSelect(FILTER_FIELDS[0], "mka-hide-narrow")}
            {renderSelect(FILTER_FIELDS[1], "mka-hide-narrow")}
            <Btn
              variant="secondary"
              icon="sliders"
              onClick={() => setIsPanelOpen(true)}
              aria-haspopup="dialog"
              className={cx("mka-toolbar__filters", activeCount > 0 && "is-active")}
            >
              {tt("filters")}
              {activeCount > 0 ? <span className="mka-toolbar__badge">{activeCount}</span> : null}
            </Btn>
            <Select
              value={sort}
              onChange={onSortChange}
              aria-label={tt("sortLabel")}
              className="mka-toolbar__select mka-toolbar__sort"
            >
              {SORT_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {tt(`sort_${value}`)}
                </option>
              ))}
            </Select>
          </>
        )}

        <div className="mka-layout-toggle" role="group" aria-label={tt("layoutLabel")}>
          <button
            type="button"
            className={cx("mka-layout-toggle__btn", layout === "grid" && "is-active")}
            aria-pressed={layout === "grid"}
            aria-label={tt("layoutGrid")}
            title={tt("layoutGrid")}
            onClick={() => onLayoutChange("grid")}
          >
            <Icon name="grid" size={16} />
          </button>
          <button
            type="button"
            className={cx("mka-layout-toggle__btn", layout === "list" && "is-active")}
            aria-pressed={layout === "list"}
            aria-label={tt("layoutList")}
            title={tt("layoutList")}
            onClick={() => onLayoutChange("list")}
          >
            <Icon name="list" size={16} />
          </button>
        </div>
      </div>

      {!searchOnly && chips.length ? (
        <div className="mka-chips" aria-label={tt("activeFilters")}>
          {chips.map(({ field, label }) => (
            <button
              key={field.key}
              type="button"
              className="mka-chip"
              onClick={() => setFilter(field.key, "")}
              aria-label={fill(tt("removeFilter"), { filter: `${tt(field.labelKey)}: ${label}` })}
            >
              <span className="mka-chip__key">{tt(field.labelKey)}</span>
              <span>{label}</span>
              <Icon name="x" size={13} />
            </button>
          ))}
          <button type="button" className="pse-link mka-chips__clear" onClick={clearFilters}>
            {tt("clearFilters")}
          </button>
        </div>
      ) : null}

      <Drawer
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        size="sm"
        icon="sliders"
        title={tt("filtersTitle")}
        description={tt("filtersDescription")}
        closeLabel={tt("close")}
        footer={
          <>
            <Btn variant="ghost" onClick={clearFilters} disabled={!activeCount}>
              {tt("clearFilters")}
            </Btn>
            <Btn variant="primary" onClick={() => setIsPanelOpen(false)}>
              {plural(tt, "showResults", resultCount)}
            </Btn>
          </>
        }
      >
        <div className="mka-filter-panel">
          {FILTER_FIELDS.map((field) => (
            <Field key={field.key} label={tt(field.labelKey)}>
              {renderSelect(field, "mka-filter-panel__select")}
            </Field>
          ))}
        </div>
      </Drawer>
    </div>
  );
};

export default ProductPickerToolbar;
